import { useEffect, useMemo, useState, type RefCallback } from 'react';

import { CSS_PREFIX } from '@/constants';

import {
  DEFAULT_VIRTUAL_SCROLL_Y,
  MIN_VIRTUAL_SCROLL_Y,
  VIRTUAL_HEADER_ALLOWANCE
} from '../../constants';

interface Params {
  virtual: boolean;
  scrollY?: number;
}

interface Result {
  tableContainerRef: RefCallback<HTMLDivElement>;
  virtualScroll: { y: number } | undefined;
}

const HEADER_SELECTOR = `.${CSS_PREFIX}-table-header`;

/**
 * Distance from the container's top edge to the bottom of the table header,
 * falling back to a fixed allowance while no header has a measurable size.
 */
const getHeaderOffset = (container: Element, header: Element | null) => {
  if (!header) return VIRTUAL_HEADER_ALLOWANCE;
  const offset =
    header.getBoundingClientRect().bottom -
    container.getBoundingClientRect().top;
  return offset > 0 ? offset : VIRTUAL_HEADER_ALLOWANCE;
};

/**
 * Tracks the available height of a virtual table's container and produces the
 * `scroll.y` passed to AntD. A fixed `scroll.y` can push rc-virtual-list's
 * horizontal scrollbar (rendered at the body's bottom edge) below the fold on
 * shorter viewports; measuring the container keeps it inside the visible area.
 *
 * The space above the body is measured from the rendered table header (which
 * also covers anything placed above the table inside the container), so a
 * header that wraps to several lines does not push the scrollbar out of view.
 * `VIRTUAL_HEADER_ALLOWANCE` is used only until a header is measurable.
 *
 * `scroll.x` is derived separately from the visible columns (see
 * `getVisibleColumnsWidth`), so it is not handled here.
 *
 * Consumer-supplied `scrollY` always wins. When `virtual` is false, no
 * measurement is performed and `virtualScroll` is `undefined`.
 */
const useVirtualScroll = ({ virtual, scrollY }: Params): Result => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [measuredScrollY, setMeasuredScrollY] = useState<number>(
    DEFAULT_VIRTUAL_SCROLL_Y
  );

  useEffect(() => {
    if (!virtual || !element) return;

    let observedHeader: Element | null = null;

    const update = () => {
      const header = element.querySelector(HEADER_SELECTOR);
      if (header !== observedHeader) {
        if (observedHeader) resizeObserver.unobserve(observedHeader);
        if (header) resizeObserver.observe(header);
        observedHeader = header;
      }
      const next = Math.max(
        MIN_VIRTUAL_SCROLL_Y,
        element.clientHeight - getHeaderOffset(element, header)
      );
      setMeasuredScrollY(prev => (prev === next ? prev : next));
    };

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    // A header mounting, unmounting or being replaced does not resize the
    // container, so DOM changes are watched too. Virtual rows mutate the
    // subtree on every scroll, hence the re-measure only on a header swap.
    const mutationObserver = new MutationObserver(() => {
      if (element.querySelector(HEADER_SELECTOR) !== observedHeader) update();
    });
    mutationObserver.observe(element, { childList: true, subtree: true });
    update();
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [virtual, element]);

  const virtualScroll = useMemo(
    () => (virtual ? { y: scrollY ?? measuredScrollY } : undefined),
    [virtual, scrollY, measuredScrollY]
  );

  return { tableContainerRef: setElement, virtualScroll };
};

export default useVirtualScroll;
